import DocumentViewerModal from "components/DocumentViewer";
import FileActions from "components/FileDownloadButtons";
import { useToast } from "contexts/ToastContext";
import { useHasRole } from "contexts/AuthContext";
import { useState } from "react";
import { editLitigation } from "services/advocateComplaintService";
import { CouncilLitigation, Litigation } from "types";
import { DateView } from "utils/helper";
import { ADVOCATE_ROLES } from "utils/roles";
import { useTranslation } from "contexts/i18nContext";
import AddLitigationFeedbackModal from "./AddLitigationFeedbackModal";
import {
  ChevronDown,
  ChevronUp,
  Calendar,
  Gavel,
  FileText,
  MessageSquare,
  Award,
  Scale,
} from "lucide-react";

interface LitigationProps {
  litigationData: Litigation[];
  councilLitigationData: CouncilLitigation[] | [];
  onLitigationRefresh: any;
}

const calculate_remaining_days = ({ date }: { date: string }) => {
  const appeal = new Date(date ?? "");
  const today = new Date();

  // Zero out time to compare dates only
  appeal.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  const msPerDay = 1000 * 60 * 60 * 24;
  const diffInMs = appeal.getTime() - today.getTime();

  const remainingDays = Math.floor(diffInMs / msPerDay);

  return remainingDays;
};

export default function LitigationCard({
  litigationData,
  councilLitigationData,
  onLitigationRefresh,
}: LitigationProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const canViewDailyHearing = useHasRole([
    ADVOCATE_ROLES.INSPECTION_PROSECUTOR,
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
  ]);
  const canAddLitigationFeedback = useHasRole([
    ADVOCATE_ROLES.INSPECTION_DIRECTOR,
    ADVOCATE_ROLES.INSPECTION_COORDINATOR,
  ]);

  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);
  const [selectedLitigation, setSelectedLitigation] = useState<any>(null);
  const [expandedCards, setExpandedCards] = useState<Set<number>>(new Set());
  const [editingDateId, setEditingDateId] = useState<number | null>(null);
  const [dateDraft, setDateDraft] = useState({
    next_appointment_date: "",
    hearing_date: "",
  });
  const [isUpdatingDate, setIsUpdatingDate] = useState(false);

  const toDateInputValue = (value?: string) => {
    if (!value) return "";
    if (/^\d{4}-\d{2}-\d{2}/.test(value)) return value.slice(0, 10);

    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return "";
    return parsed.toISOString().slice(0, 10);
  };

  const startDateEdit = (obj: any) => {
    setEditingDateId(obj?.id ?? null);
    setDateDraft({
      next_appointment_date: toDateInputValue(obj?.next_appointment_date),
      hearing_date: toDateInputValue(obj?.hearing_date),
    });
  };

  const cancelDateEdit = () => {
    setEditingDateId(null);
    setDateDraft({ next_appointment_date: "", hearing_date: "" });
  };

  const handleUpdateDate = async (obj: any) => {
    if (!obj?.id) {
      showToast("Litigation record not found", "error");
      return;
    }

    const currentNext = toDateInputValue(obj?.next_appointment_date);
    const currentHearing = toDateInputValue(obj?.hearing_date);

    const payload: any = {};
    if (
      dateDraft.next_appointment_date &&
      dateDraft.next_appointment_date !== currentNext
    ) {
      payload.next_appointment_date = dateDraft.next_appointment_date;
    }
    if (dateDraft.hearing_date && dateDraft.hearing_date !== currentHearing) {
      payload.hearing_date = dateDraft.hearing_date;
    }

    if (!payload.next_appointment_date && !payload.hearing_date) {
      showToast("No date change detected", "error");
      return;
    }

    setIsUpdatingDate(true);
    try {
      await editLitigation(payload, String(obj.id), false);
      await Promise.resolve(onLitigationRefresh?.());
      cancelDateEdit();
      showToast("Date updated", "success");
    } catch (err) {
      showToast("Failed to update date", "error");
    } finally {
      setIsUpdatingDate(false);
    }
  };

  const toggleCard = (id: number) => {
    const newExpanded = new Set(expandedCards);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedCards(newExpanded);
  };

  const getStatusBadge = (obj: any) => {
    if (obj?.council_decision_summary) {
      return (
        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-dark-red-50 dark:bg-dark-red-900/30 border border-dark-red-200 dark:border-dark-red-700 text-dark-red-700 dark:text-dark-red-300 text-xs font-medium">
          <Award className="w-3 h-3" />
          {t("decisionMade")}
        </span>
      );
    }

    if (obj?.next_appointment_date) {
      const appointmentDate = new Date(obj.next_appointment_date);
      const today = new Date();
      const diffTime = appointmentDate.getTime() - today.getTime();
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays < 0) {
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-700 text-red-700 dark:text-red-300 text-xs font-medium">
            <Calendar className="w-3 h-3" />
            {t("overdue")}
          </span>
        );
      } else if (diffDays <= 7) {
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-amber-700 dark:text-amber-300 text-xs font-medium">
            <Calendar className="w-3 h-3" />
            {t("upcoming")}
          </span>
        );
      }
    }

    return (
      <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 text-blue-700 dark:text-blue-300 text-xs font-medium">
        <Calendar className="w-3 h-3" />
        {t("scheduled")}
      </span>
    );
  };

  return (
    <div className="mb-8 space-y-4">
      {[...(litigationData || [])]
        ?.sort((a, b) => {
          const aHasDecision = !!a.council_decision_summary;
          const bHasDecision = !!b.council_decision_summary;

          // If only one has a decision, show it first
          if (aHasDecision && !bHasDecision) return -1;
          if (!aHasDecision && bHasDecision) return 1;

          // Otherwise, sort by next appointment date descending
          const dateA = new Date(a?.next_appointment_date ?? "").getTime();
          const dateB = new Date(b?.next_appointment_date ?? "").getTime();

          return dateA - dateB;
        })
        ?.map((obj: any) =>
          canViewDailyHearing ? (
            <div
              key={obj?.id}
              className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border transition-all duration-200 ${
                obj?.council_decision_summary
                  ? "border-l-4 border-dark-red-500 dark:border-dark-red-400"
                  : "border-l-4 border-gray-200 dark:border-gray-700"
              } hover:shadow-md`}
            >
              {/* Collapsible Header */}
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 rounded-t-xl transition-colors"
                onClick={() => toggleCard(obj?.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div
                      className={`p-2 rounded-lg ${
                        obj?.council_decision_summary
                          ? "bg-dark-red-50 dark:bg-dark-red-900/20"
                          : "bg-gray-100 dark:bg-gray-700"
                      }`}
                    >
                      <Gavel
                        className={`w-5 h-5 ${
                          obj?.council_decision_summary
                            ? "text-dark-red-600 dark:text-dark-red-400"
                            : "text-gray-600 dark:text-gray-400"
                        }`}
                      />
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        {getStatusBadge(obj)}
                      </div>
                      {obj?.next_appointment_date && (
                        <div className="flex items-center gap-2 mt-1">
                          <Calendar className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                            {t("nextAppointment")}:{" "}
                            {DateView(obj.next_appointment_date)}
                          </span>
                          {obj?.hearing_date && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              • {t("hearing")}: {DateView(obj.hearing_date)}
                            </span>
                          )}
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              startDateEdit(obj);
                            }}
                            className="text-xs font-medium text-red-700 hover:text-red-800 underline"
                          >
                            {t("updateDate")}
                          </button>
                        </div>
                      )}

                      {editingDateId === obj?.id && (
                        <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3 space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                {t("nextAppointment")}
                              </label>
                              <input
                                type="date"
                                value={dateDraft.next_appointment_date}
                                onChange={(e) =>
                                  setDateDraft((prev) => ({
                                    ...prev,
                                    next_appointment_date: e.target.value,
                                  }))
                                }
                                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                              />
                            </div>
                            <div>
                              <label className="block text-xs font-medium text-gray-600 mb-1">
                                {t("hearingDate")}
                              </label>
                              <input
                                type="date"
                                value={dateDraft.hearing_date}
                                onChange={(e) =>
                                  setDateDraft((prev) => ({
                                    ...prev,
                                    hearing_date: e.target.value,
                                  }))
                                }
                                className="w-full rounded border border-gray-300 px-2 py-1.5 text-sm"
                              />
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              disabled={isUpdatingDate}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleUpdateDate(obj);
                              }}
                              className="rounded bg-red-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-800 disabled:opacity-60"
                            >
                              {isUpdatingDate ? "Updating..." : "Save Date"}
                            </button>
                            <button
                              type="button"
                              disabled={isUpdatingDate}
                              onClick={(e) => {
                                e.stopPropagation();
                                cancelDateEdit();
                              }}
                              className="rounded border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-100"
                            >
                              {t("cancel")}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-4">
                    {obj?.assigned_prosecutor && (
                      <div className="hidden md:flex items-center gap-2">
                        <img
                          src={require("../../../images/avatar.png")}
                          alt="profile"
                          className="w-8 h-8 rounded-full object-cover"
                        />
                        <div className="text-right">
                          <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                            {obj.assigned_prosecutor}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {t("prosecutor")}
                          </p>
                        </div>
                      </div>
                    )}

                    {expandedCards.has(obj?.id) ? (
                      <ChevronUp className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400 dark:text-gray-500" />
                    )}
                  </div>
                </div>
              </div>

              {/* Collapsible Content */}
              {expandedCards.has(obj?.id) && (
                <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-700 pt-4 space-y-4">
                  {/* Prosecutor Info (Mobile) */}
                  {obj?.assigned_prosecutor && (
                    <div className="md:hidden flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg">
                      <img
                        src={require("../../../images/avatar.png")}
                        alt="profile"
                        className="w-10 h-10 rounded-full object-cover"
                      />
                      <div>
                        <h2 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                          {obj?.assigned_prosecutor || "-"}
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t("assignedProsecutor")}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Key Information Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {obj?.hearing_date && (
                      <div className="bg-gray-50 dark:bg-gray-700/30 p-3 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {t("hearingDate")}
                          </p>
                        </div>
                        <p className="text-base font-medium text-gray-800 dark:text-gray-100">
                          {DateView(obj.hearing_date)}
                        </p>
                      </div>
                    )}

                    {obj?.given_order && (
                      <div className="bg-gray-50 dark:bg-gray-700/30 p-3 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <Scale className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {t("councilOrder")}
                          </p>
                        </div>
                        <p className="text-base font-medium text-gray-800 dark:text-gray-100">
                          {obj.given_order}
                        </p>
                      </div>
                    )}

                    {obj?.prosecutor_opinion_on_decision_appeal && (
                      <div className="bg-gray-50 dark:bg-gray-700/30 p-3 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <MessageSquare className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {t("prosecutorOpinion")}
                          </p>
                        </div>
                        <p className="text-base font-medium text-gray-800 dark:text-gray-100">
                          {obj.prosecutor_opinion_on_decision_appeal}
                        </p>
                      </div>
                    )}

                    {councilLitigationData[0]?.council_decision_day && (
                      <div className="bg-gray-50 dark:bg-gray-700/30 p-3 rounded-lg">
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          <p className="text-sm text-gray-600 dark:text-gray-400">
                            {t("appealDaysRemaining")}
                          </p>
                        </div>
                        {(() => {
                          const days = calculate_remaining_days({
                            date:
                              councilLitigationData[0]?.council_decision_day ??
                              "",
                          });
                          const isExpired = days < 0;
                          const absDays = Math.abs(days);
                          const dayLabel = absDays === 1 ? t("day") : t("days");

                          if (!councilLitigationData[0]?.council_decision_day)
                            return (
                              <p className="text-base font-medium text-gray-800 dark:text-gray-100">
                                -
                              </p>
                            );

                          return (
                            <p
                              className={`text-base font-medium ${
                                isExpired
                                  ? "text-red-600 dark:text-red-400"
                                  : "text-green-600 dark:text-green-400"
                              }`}
                            >
                              {isExpired
                                ? `${t("expired")} ${absDays} ${dayLabel} ${t("ago")}`
                                : `${days} ${dayLabel} ${t("remaining")}`}
                            </p>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  {/* Detailed Sections */}
                  {obj?.daily_report_summary && (
                    <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <FileText className="w-4 h-4 text-[#002855]" />
                        <p className="text-[#002855] dark:text-blue-300 font-medium">
                          {t("dailyReportSummary")}
                        </p>
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 mt-1">
                        {obj.daily_report_summary}
                      </p>
                    </div>
                  )}

                  {obj?.appointment_reason && (
                    <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <MessageSquare className="w-4 h-4 text-[#002855]" />
                        <p className="text-[#002855] dark:text-blue-300 font-medium">
                          {t("appointmentReason")}
                        </p>
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 mt-1">
                        {obj.appointment_reason}
                      </p>
                    </div>
                  )}

                  {obj?.prosecutor_opinion_on_decision && (
                    <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <MessageSquare className="w-4 h-4 text-[#002855]" />
                        <p className="text-[#002855] dark:text-blue-300 font-medium">
                          {t("prosecutorOpinionOnDecision")}
                        </p>
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 mt-1">
                        {obj.prosecutor_opinion_on_decision}
                      </p>
                    </div>
                  )}

                  {obj?.director_feedbacks?.map(
                    (feedback: any, index: number) => (
                      <div
                        key={index}
                        className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-100 dark:border-blue-800"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <MessageSquare className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                          <p className="text-blue-600 dark:text-blue-400 font-medium">
                            {t("feedback")}
                          </p>
                        </div>
                        <p className="text-gray-700 dark:text-gray-300 mt-1">
                          {feedback?.comment || "-"}
                        </p>
                      </div>
                    ),
                  )}

                  {obj?.council_decision_summary && (
                    <div className="bg-dark-red-50 dark:bg-dark-red-900/20 p-4 rounded-lg border border-dark-red-100 dark:border-dark-red-800">
                      <div className="flex items-center gap-2 mb-2">
                        <Award className="w-4 h-4 text-dark-red-600 dark:text-dark-red-400" />
                        <p className="text-dark-red-600 dark:text-dark-red-400 font-medium">
                          {t("councilDecisionSummary")}
                        </p>
                      </div>
                      <p className="text-gray-700 dark:text-gray-300 mt-1">
                        {obj.council_decision_summary}
                      </p>
                    </div>
                  )}

                  {(obj?.council_decision_type ||
                    obj?.discipline_measure_type) && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {obj?.council_decision_type && (
                        <div className="bg-gray-50 dark:bg-gray-700/30 p-3 rounded-lg">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                            {t("councilDecisionType")}
                          </p>
                          <p className="text-base font-medium text-gray-800 dark:text-gray-100">
                            {obj.council_decision_type}
                          </p>
                        </div>
                      )}
                      {obj?.discipline_measure_type && (
                        <div className="bg-gray-50 dark:bg-gray-700/30 p-3 rounded-lg">
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
                            {t("decisionMeasureType")}
                          </p>
                          <p className="text-base font-medium text-gray-800 dark:text-gray-100">
                            {obj.discipline_measure_type}
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Files */}
                  <div className="space-y-3">
                    <DocumentViewerModal
                      documents={obj?.documents || []}
                      baseUrl={`${process.env.REACT_APP_API_BASE_URL}prosecutor-decipiline/investigation-documents/document`}
                      layout="list"
                      hideEmptyFile={true}
                    />

                    {obj?.termination_document && (
                      <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg border border-gray-200 dark:border-gray-600">
                        <div className="flex items-center gap-3">
                          <div className="bg-[#5e3b1f] text-white font-bold text-xs px-2 py-1 rounded">
                            PDF
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-800 dark:text-gray-100">
                              {t("terminationFile")}
                            </p>
                          </div>
                        </div>
                        <FileActions
                          fileUrl={obj?.termination_document || ""}
                        />
                      </div>
                    )}
                  </div>

                  {/* Feedback Button */}
                  {canAddLitigationFeedback &&
                    obj?.hearing_date &&
                    obj?.director_feedbacks &&
                    obj?.director_feedbacks.length <= 0 && (
                      <button
                        className="w-full bg-dark-red-600 hover:bg-dark-red-700 text-white py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 mt-2"
                        onClick={() => {
                          setSelectedLitigation(obj);
                          setIsFeedbackOpen(true);
                        }}
                      >
                        <MessageSquare className="w-4 h-4" />
                        {t("addFeedback")}
                      </button>
                    )}
                </div>
              )}
            </div>
          ) : (
            <></>
          ),
        )}

      {litigationData && (
        <AddLitigationFeedbackModal
          isOpen={isFeedbackOpen}
          setIsOpen={setIsFeedbackOpen}
          litigationData={selectedLitigation}
          onRefresh={onLitigationRefresh}
        />
      )}
    </div>
  );
}
